// src/pages/Admin/Orders/OrderDetail.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, RefreshCw, Printer, Download, Truck, Package, User, MapPin,
  Phone, Mail, Calendar, CreditCard, CheckCircle, XCircle, AlertCircle,
  ChevronDown, ChevronUp, Edit, Save, X, UserPlus, History, FileText
} from 'lucide-react';
import { ordersAPI } from '../../../api/orders';
import { fulfillmentAPI } from '../../../api/fulfillment';
import { userAPI } from '../../../api/user';
import { formatPrice } from '../../../utils/utilityFunctions';
import { formatDateTimeEnglish, getTimeAgo } from '../../../utils/date';
import { formatPhoneNumber } from '../../../utils/phoneFormatter';
import OrderStatusBadge from './components/OrderStatusBadge';
import showToast from '../../../utils/toast';

interface OrderDetail {
  id: number;
  order_number: string;
  customer_name: string;
  customer_phone: string;
  customer_email: string;
  status: string;
  total: number;
  subtotal: number;
  delivery_fee: number;
  payment_method: string;
  payment_status: string;
  shipping_address_label?: string;
  shipping_address_line_1: string;
  shipping_address_line_2: string | null;
  shipping_city_name: string;
  shipping_house_number: string;
  shipping_recipient_name: string;
  shipping_contact_number: string;
  shipping_contact_number_2?: string;
  shipping_delivery_note: string | null;
  delivery_option: string;
  tracking_number: string | null;
  created_at: string;
  items: {
    id: number;
    product_name: string;
    product_variant_name: string;
    quantity: number;
    price_at_sale: number;
    total_price: number;
    product_image: string;
  }[];
  shipments: {
    id: string;
    warehouse_name: string;
    warehouse_id: number;
    status: string;
    tracking_number: string;
    packages: {
      id: string;
      package_number: number;
      status: string;
      tracking_number?: string;
      driver_name?: string;
      driver_contact?: string;
      driver_assigned_at?: string;
      items: {
        id: string;
        product_name: string;
        quantity: number;
        price: number;
        product_image: string;
      }[];
    }[];
  }[];
  status_history?: {
    id: number;
    status: string;
    notes: string;
    changed_by: string;
    created_at: string;
  }[];
}

const OrderDetail: React.FC = () => {
  const { orderNumber } = useParams<{ orderNumber: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedShipment, setExpandedShipment] = useState<string | null>(null);
  const [showStatusUpdate, setShowStatusUpdate] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [showDriverAssign, setShowDriverAssign] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<string | null>(null);
  const [availableDrivers, setAvailableDrivers] = useState<any[]>([]);
  const [selectedDriver, setSelectedDriver] = useState<number | null>(null);
  const [isAssigning, setIsAssigning] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [isCancelling, setIsCancelling] = useState(false);
  const [assigningPackageId, setAssigningPackageId] = useState<string | null>(null);
  const [driverSearch, setDriverSearch] = useState('');
  const [showDriverList, setShowDriverList] = useState(false);
  const [selectedDriverId, setSelectedDriverId] = useState<number | null>(null);

  // Filter drivers by search
  const filteredDrivers = availableDrivers.filter(driver =>
    driver.name.toLowerCase().includes(driverSearch.toLowerCase()) ||
    driver.email.toLowerCase().includes(driverSearch.toLowerCase())
  );

  // Open inline assignment for a package
  const openInlineAssignment = (packageId: string) => {
    setAssigningPackageId(packageId);
    setShowDriverList(true);
    setDriverSearch('');
    setSelectedDriverId(null);
    fetchAvailableDrivers();
  };

  // Handle inline assignment
  const handleInlineAssign = async () => {
    if (!assigningPackageId || !selectedDriverId) return;

    setIsAssigning(true);
    try {
      const response = await fulfillmentAPI.assignDriverToPackage(assigningPackageId, selectedDriverId);
      if (!response.error) {
        await fetchOrderDetail();
        setShowDriverList(false);
        setAssigningPackageId(null);
        setSelectedDriverId(null);
        setDriverSearch('');
        showToast('Driver assigned successfully', 'success');
      }
    } catch (error) {
      console.error('Failed to assign driver:', error);
      showToast('Failed to assign driver', 'error');
    } finally {
      setIsAssigning(false);
    }
  };

  useEffect(() => {
    if (orderNumber) {
      fetchOrderDetail();
    }
  }, [orderNumber]);

  const fetchOrderDetail = async () => {
    setLoading(true);
    try {
      const response = await ordersAPI.getAdminOrder(orderNumber!);
      if (!response.error && response.data) {
        setOrder(response.data);
        setNewStatus(response.data.status);
      }
    } catch (error) {
      console.error('Failed to fetch order details:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableDrivers = async () => {
    try {
      const response = await userAPI.getAvailableDrivers();
      console.log('Drivers response:', response);  // Add this
      if (!response.error && response.data) {
        console.log('Drivers data:', response.data);  // Add this
        setAvailableDrivers(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch drivers:', error);
    }
  };

  const handleStatusUpdate = async () => {
    if (!order || newStatus === order.status) return;

    try {
      const response = await ordersAPI.updateOrderStatus(order.id, newStatus);
      if (!response.error) {
        setOrder({ ...order, status: newStatus });
        setShowStatusUpdate(false);
        showToast('Status updated successfully', 'success');
      }
    } catch (error) {
      console.error('Failed to update status:', error);
      showToast('Failed to update status', 'error');
    }
  };

  const handleAssignDriverToPackage = async () => {
    if (!selectedPackage || !selectedDriver) return;

    setIsAssigning(true);
    try {
      const response = await fulfillmentAPI.assignDriverToPackage(selectedPackage, selectedDriver);
      if (!response.error) {
        await fetchOrderDetail();
        setShowDriverAssign(false);
        setSelectedDriver(null);
        setSelectedPackage(null);
        showToast('Driver assigned successfully', 'success');
      }
    } catch (error) {
      console.error('Failed to assign driver:', error);
      showToast('Failed to assign driver', 'error');
    } finally {
      setIsAssigning(false);
    }
  };

  const handleMarkShipped = async (packageId: string) => {
    try {
      const response = await fulfillmentAPI.markPackageShipped(packageId);
      if (!response.error) {
        await fetchOrderDetail();
        showToast('Package marked as shipped', 'success');
      }
    } catch (error) {
      console.error('Failed to mark package as shipped:', error);
      showToast('Failed to mark package as shipped', 'error');
    }
  };

  const handleMarkDelivered = async (packageId: string) => {
    try {
      const response = await fulfillmentAPI.markPackageDelivered(packageId);
      if (!response.error) {
        await fetchOrderDetail();
        showToast('Package marked as delivered', 'success');
      }
    } catch (error) {
      console.error('Failed to mark package as delivered:', error);
      showToast('Failed to mark package as delivered', 'error');
    }
  };

  const handleCancelOrder = async () => {
    if (!order || !cancelReason.trim()) return;

    setIsCancelling(true);
    try {
      const response = await ordersAPI.cancelOrder(order.id, cancelReason);
      if (!response.error) {
        await fetchOrderDetail();
        setShowCancelModal(false);
        setCancelReason('');
        showToast('Order cancelled successfully', 'success');
      }
    } catch (error) {
      console.error('Failed to cancel order:', error);
      showToast('Failed to cancel order', 'error');
    } finally {
      setIsCancelling(false);
    }
  };

  const toggleShipment = (shipmentId: string) => {
    setExpandedShipment(expandedShipment === shipmentId ? null : shipmentId);
  };

  const getStatusOptions = () => {
    return ['confirmed', 'processing', 'packed', 'out_for_delivery', 'delivered', 'cancelled'];
  };

  const canCancel = () => {
    if (!order) return false;
    return ['confirmed', 'processing', 'packed'].includes(order.status);
  };

  const openDriverAssign = (packageId: string) => {
    setSelectedPackage(packageId);
    setShowDriverAssign(true);
    fetchAvailableDrivers();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="text-center py-12">
        <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500">Order not found</p>
        <button
          onClick={() => navigate('/dashboard/orders')}
          className="mt-4 text-blue-600 hover:text-blue-700"
        >
          ← Back to Orders
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header - same as before */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/dashboard/orders')}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Order #{order.order_number}
            </h1>
            <p className="text-sm text-gray-500">
              {formatDateTimeEnglish(order.created_at)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={fetchOrderDetail} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg">
            <RefreshCw className="w-5 h-5" />
          </button>
          <button onClick={() => window.print()} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg">
            <Printer className="w-5 h-5" />
          </button>
          {canCancel() && (
            <button
              onClick={() => setShowCancelModal(true)}
              className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 flex items-center gap-2"
            >
              <XCircle className="w-4 h-4" />
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* Status Bar - same as before */}
      <div className="flex flex-wrap items-center gap-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">Status:</span>
          <OrderStatusBadge status={order.status} size="md" />
        </div>
        <button
          onClick={() => setShowStatusUpdate(!showStatusUpdate)}
          className="ml-auto flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700"
        >
          <Edit className="w-4 h-4" />
          Update Status
        </button>
      </div>

      {/* Status Update Form */}
      {showStatusUpdate && (
        <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
          <div className="flex flex-wrap items-center gap-4">
            <select
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              {getStatusOptions().map((status) => (
                <option key={status} value={status}>
                  {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
                </option>
              ))}
            </select>
            <button
              onClick={handleStatusUpdate}
              disabled={newStatus === order.status}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <Save className="w-4 h-4 inline mr-1.5" />
              Update
            </button>
            <button
              onClick={() => setShowStatusUpdate(false)}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Driver Assignment Modal */}
      {showDriverAssign && (
        <div className="p-4 bg-indigo-50 rounded-xl border border-indigo-200">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-1">Select Driver</label>
              <select
                value={selectedDriver || ''}
                onChange={(e) => setSelectedDriver(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Choose a driver...</option>
                {availableDrivers.map((driver) => (
                  <option key={driver.id} value={driver.id}>
                    {driver.name} - {driver.zone || 'Unassigned'}
                  </option>
                ))}
              </select>
            </div>
            <button
              onClick={handleAssignDriverToPackage}
              disabled={!selectedDriver || isAssigning}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {isAssigning ? 'Assigning...' : 'Assign Driver'}
            </button>
            <button
              onClick={() => {
                setShowDriverAssign(false);
                setSelectedDriver(null);
                setSelectedPackage(null);
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Order Summary Grid - same as before */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Customer Info */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4 flex items-center gap-2">
            <User className="w-4 h-4" />
            Customer
          </h3>
          <div className="space-y-3">
            <p className="font-medium text-gray-900">{order.customer_name}</p>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Mail className="w-4 h-4 text-gray-400" />
              {order.customer_email || 'N/A'}
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Phone className="w-4 h-4 text-gray-400" />
              {formatPhoneNumber(order.customer_phone)}
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Calendar className="w-4 h-4 text-gray-400" />
              {formatDateTimeEnglish(order.created_at)}
            </div>
          </div>
        </div>

        {/* Shipping Info */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4 flex items-center gap-2">
            <MapPin className="w-4 h-4" />
            Shipping Address
          </h3>
          <div className="space-y-2 text-sm">
            {order.shipping_address_label && (
              <span className="inline-block px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full mb-1">
                {order.shipping_address_label}
              </span>
            )}
            <p className="font-medium text-gray-900">
              {order.shipping_recipient_name}
            </p>
            <p className="text-gray-600">
              {order.shipping_house_number} {order.shipping_address_line_1}
            </p>
            {order.shipping_address_line_2 && (
              <p className="text-gray-600">{order.shipping_address_line_2}</p>
            )}
            <p className="text-gray-600">{order.shipping_city_name}</p>
            <div className="flex items-center gap-2 text-gray-600">
              <Phone className="w-4 h-4 text-gray-400" />
              {formatPhoneNumber(order.shipping_contact_number)}
            </div>
            {order.shipping_contact_number_2 && (
              <div className="flex items-center gap-2 text-gray-600">
                <Phone className="w-4 h-4 text-gray-400" />
                {formatPhoneNumber(order.shipping_contact_number_2)} (Secondary)
              </div>
            )}
            {order.shipping_delivery_note && (
              <div className="mt-2 p-2 bg-yellow-50 rounded text-xs text-yellow-700">
                📝 {order.shipping_delivery_note}
              </div>
            )}
          </div>
        </div>

        {/* Payment & Delivery Info */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4 flex items-center gap-2">
            <CreditCard className="w-4 h-4" />
            Payment & Delivery
          </h3>
          <div className="space-y-3">
            <div>
              <p className="text-xs text-gray-500">Payment Method</p>
              <p className="font-medium text-gray-900 capitalize">
                {order.payment_method.replace(/_/g, ' ')}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Payment Status</p>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${order.payment_status === 'paid'
                ? 'bg-green-100 text-green-700'
                : 'bg-yellow-100 text-yellow-700'
                }`}>
                {order.payment_status}
              </span>
            </div>
            <div>
              <p className="text-xs text-gray-500">Delivery Option</p>
              <p className="font-medium text-gray-900 capitalize">{order.delivery_option}</p>
            </div>
            <div className="pt-3 border-t border-gray-100">
              <p className="text-xs text-gray-500">Order Total</p>
              <p className="text-xl font-bold text-gray-900">{formatPrice(order.total)}</p>
              <div className="text-sm text-gray-500">
                Subtotal: {formatPrice(order.subtotal)} + Delivery: {formatPrice(order.delivery_fee)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Items Section */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider flex items-center gap-2">
            <Package className="w-4 h-4" />
            Items ({order.items.length})
          </h3>
        </div>
        <div className="divide-y divide-gray-100">
          {order.items.map((item) => (
            <div key={item.id} className="flex items-center gap-4 p-4 hover:bg-gray-50">
              <div className="w-12 h-12 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                {item.product_image ? (
                  <img
                    src={item.product_image}
                    alt={item.product_name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Package className="w-full h-full p-2 text-gray-400" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 truncate">{item.product_name}</p>
                {item.product_variant_name && (
                  <p className="text-sm text-gray-500">{item.product_variant_name}</p>
                )}
              </div>
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{formatPrice(item.total_price)}</p>
                <p className="text-xs text-gray-500">{item.quantity} × {formatPrice(item.price_at_sale)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Shipments Section - Updated with driver assignment */}
      {order.shipments && order.shipments.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider flex items-center gap-2">
              <Truck className="w-4 h-4" />
              Shipments ({order.shipments.length})
            </h3>
          </div>
          <div className="divide-y divide-gray-100">
            {order.shipments.map((shipment) => (
              <div key={shipment.id} className="p-4">
                <div
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => toggleShipment(shipment.id)}
                >
                  <div className="flex items-center gap-3">
                    <span className="font-medium text-gray-900">
                      {shipment.warehouse_name}
                    </span>
                    <span className="text-sm text-gray-500">
                      {shipment.packages.length} package(s)
                    </span>
                    <OrderStatusBadge status={shipment.status} size="sm" />
                  </div>
                  <div className="flex items-center gap-2">
                    {shipment.tracking_number && (
                      <span className="text-xs font-mono text-gray-500">{shipment.tracking_number}</span>
                    )}
                    {expandedShipment === shipment.id ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>

                {expandedShipment === shipment.id && (
                  <div className="mt-4 pl-4 border-l-2 border-gray-200 space-y-3">
                    {shipment.packages.map((pkg) => (
                      <div key={pkg.id} className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                        {/* Package Header */}
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-700">
                              Package #{pkg.package_number}
                            </span>
                            <OrderStatusBadge status={pkg.status} size="sm" />
                          </div>
                          {pkg.tracking_number && (
                            <span className="text-xs font-mono text-gray-400">{pkg.tracking_number}</span>
                          )}
                        </div>

                        {/* Driver Assignment - Inline */}
                        <div className="mb-2 p-2 bg-white rounded border border-gray-100">
                          {assigningPackageId === pkg.id && showDriverList ? (
                            // Inline assignment UI
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <Truck className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                <span className="text-sm font-medium text-gray-700">Assign Driver</span>
                              </div>

                              {/* Search input */}
                              <div className="relative">
                                <input
                                  type="text"
                                  placeholder="Search drivers..."
                                  value={driverSearch}
                                  onChange={(e) => setDriverSearch(e.target.value)}
                                  className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                  autoFocus
                                />
                              </div>

                              {/* Driver list */}
                              <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                                {filteredDrivers.length === 0 ? (
                                  <div className="px-3 py-2 text-sm text-gray-500">
                                    {availableDrivers.length === 0 ? 'No drivers available' : 'No matching drivers'}
                                  </div>
                                ) : (
                                  filteredDrivers.map((driver) => (
                                    <div
                                      key={driver.id}
                                      onClick={() => setSelectedDriverId(driver.id)}
                                      className={`flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-gray-50 transition-colors ${selectedDriverId === driver.id ? 'bg-blue-50' : ''
                                        }`}
                                    >
                                      <div>
                                        <p className="text-sm font-medium text-gray-900">{driver.name}</p>
                                        <p className="text-xs text-gray-500">{driver.email}</p>
                                      </div>
                                      {selectedDriverId === driver.id && (
                                        <CheckCircle className="w-4 h-4 text-blue-600" />
                                      )}
                                    </div>
                                  ))
                                )}
                              </div>

                              {/* Actions */}
                              <div className="flex items-center gap-2 pt-1">
                                <button
                                  onClick={handleInlineAssign}
                                  disabled={!selectedDriverId || isAssigning}
                                  className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
                                >
                                  {isAssigning ? 'Assigning...' : 'Assign'}
                                </button>
                                <button
                                  onClick={() => {
                                    setShowDriverList(false);
                                    setAssigningPackageId(null);
                                    setSelectedDriverId(null);
                                    setDriverSearch('');
                                  }}
                                  className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : pkg.driver_name ? (
                            // Driver already assigned
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Truck className="w-4 h-4 text-green-600" />
                                <span className="text-sm text-gray-700">
                                  Driver: <span className="font-medium">{pkg.driver_name}</span>
                                </span>
                                {pkg.driver_assigned_at && (
                                  <span className="text-xs text-gray-400">
                                    ({getTimeAgo(pkg.driver_assigned_at)})
                                  </span>
                                )}
                              </div>
                              <button
                                onClick={() => openInlineAssignment(pkg.id)}
                                className="text-xs text-blue-600 hover:text-blue-700"
                              >
                                Change
                              </button>
                            </div>
                          ) : (
                            // No driver assigned - show assign button
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Truck className="w-4 h-4 text-gray-400" />
                                <span className="text-sm text-gray-400">No driver assigned</span>
                              </div>
                              <button
                                onClick={() => openInlineAssignment(pkg.id)}
                                className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                              >
                                <UserPlus className="w-3.5 h-3.5" />
                                Assign Driver
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Package Items */}
                        <div className="space-y-2">
                          {pkg.items.map((item) => (
                            <div key={item.id} className="flex items-center gap-3 text-sm">
                              <span className="text-gray-600">{item.product_name}</span>
                              <span className="text-gray-400">×</span>
                              <span className="text-gray-900">{item.quantity}</span>
                            </div>
                          ))}
                        </div>

                        {/* Package Actions */}
                        <div className="mt-3 pt-2 border-t border-gray-100 flex items-center gap-2">
                          {pkg.status === 'packed' && (
                            <button
                              onClick={() => handleMarkShipped(pkg.id)}
                              className="text-xs bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700"
                            >
                              Mark Shipped
                            </button>
                          )}
                          {pkg.status === 'shipped' && (
                            <button
                              onClick={() => handleMarkDelivered(pkg.id)}
                              className="text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                            >
                              Mark Delivered
                            </button>
                          )}
                          {pkg.status === 'out_for_delivery' && (
                            <button
                              onClick={() => handleMarkDelivered(pkg.id)}
                              className="text-xs bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700"
                            >
                              Confirm Delivery
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Cancel Order Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowCancelModal(false)} />
            <div className="relative bg-white rounded-lg max-w-md w-full p-6">
              <div className="flex items-start gap-3 mb-4">
                <div className="p-2 bg-red-100 rounded-full">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Cancel Order</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Are you sure you want to cancel this order? This action cannot be undone.
                  </p>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason for cancellation <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  placeholder="Please provide a reason for cancelling..."
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowCancelModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Keep Order
                </button>
                <button
                  onClick={handleCancelOrder}
                  disabled={!cancelReason.trim() || isCancelling}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {isCancelling ? 'Cancelling...' : 'Cancel Order'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderDetail;